import Icon from '@atoms/Icon/Icon';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Text from '@atoms/Text/Text';
import { ErrorMessage } from '@hookform/error-message';
import { ISearch } from '@models/ISearch';
import SizeEnum from '@models/enums/SizeEnum';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useAsyncList } from 'react-stately';
import HighlightedResult from './HighlightedResult';

const Search = ({
  autoCompleteEndpoint,
  autoFocus = false,
  backgroundColor = 'tw-bg-white dark:tw-bg-grey-10',
  dataTestId,
  errors,
  initialValue,
  label,
  large = false,
  list,
  name = '',
  predictiveCharacterLength = 3,
  register,
  requiredErrorMessage,
  searchClasses,
  setValue,
  darkMode = false,
  onAutoCompleteSelect,
  clearSearch,
  ...rest
}: ISearch) => {
  const [inputValue, setInputValue] = useState<string>('');

  const suggestions = useAsyncList<string, string>({
    async load({ signal, filterText }) {
      if (list) {
        return {
          items: list,
        };
      } else if (filterText && filterText.length >= predictiveCharacterLength) {
        const res = await fetch(`${autoCompleteEndpoint}=${filterText}`, {
          signal,
        });

        const json = await res.json();

        return {
          items: json,
        };
      }

      return {
        items: [],
      };
    },
  });

  const { items, isLoading } = suggestions;

  const debouncedValueChange = useRef(
    debounce((text: string) => {
      suggestions.setFilterText(text);
    }, 500)
  ).current;

  const handleValueChange = ({ inputValue }: { inputValue?: string }) => {
    const next = inputValue ?? '';
    setInputValue(next);
    setValue?.(name ?? '', next, { shouldValidate: true });
    debouncedValueChange(next);
  };

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    reset,
  } = useCombobox({
    onInputValueChange: handleValueChange,
    initialInputValue: initialValue ?? '',
    items,
  });

  useEffect(() => {
    if (selectedItem) {
      onAutoCompleteSelect?.(selectedItem);

      // On dropdown select, blur the input (hides the keyboard on iOS)
      (document?.activeElement as HTMLElement)?.blur();
    }
  }, [selectedItem]);

  const showDropdown =
    isOpen && inputValue.length >= predictiveCharacterLength && !!items.length;

  const hasErrors = errors && name in errors;

  useEffect(() => {
    if (clearSearch && clearSearch > 0) reset();
  }, [clearSearch]);

  return (
    <div className="tw-relative">
      <div
        className={classNames(
          'tw-group tw-flex tw-w-full tw-flex-col tw-rounded tw-border tw-bg-transparent tw-px-xs tw-py-3.5',
          large ? 'tw-text-xl tw-font-light' : 'tw-text-base',
          {
            'tw-border-red': errors && name in errors,
            'tw-border-grey-60 focus-within:tw-border-black':
              !hasErrors && !darkMode,
            'tw-border-grey-30 focus-within:tw-border-white':
              !hasErrors && darkMode,
          },
          backgroundColor,
          searchClasses
        )}
      >
        <label htmlFor={name} className="tw-sr-only" {...getLabelProps()}>
          {label}
        </label>
        <div className="tw-flex tw-gap-0.5">
          <input
            value="initial"
            type="text"
            autoFocus={autoFocus}
            className={classNames(
              'tw-w-full tw-grow tw-bg-transparent tw-outline-none',
              darkMode && 'placeholder:tw-text-grey-30'
            )}
            {...rest}
            {...getInputProps()}
            onKeyDown={(e) => {
              // Workaround to allow form submission when nothing in dropdown is selected
              const isEnter = e.key === 'Enter';
              const hasHighlightedItem = highlightedIndex > -1;

              // Let Downshift handle everything except the "submit form" case
              if (!isEnter || hasHighlightedItem) {
                getInputProps()?.onKeyDown?.(e);
              }

              if (!isEnter) return;

              if (hasHighlightedItem) {
                e.preventDefault();
                return;
              }

              const inputEl = e.currentTarget;
              const current = inputEl.value;

              setInputValue(current);
              setValue?.(name ?? '', current, { shouldValidate: true });

              e.preventDefault(); // prevent double submit
              inputEl.form?.requestSubmit?.();

              // Close the keyboard after pressing return on mobile
              setTimeout(() => inputEl.blur(), 0);
            }}
            data-testid={dataTestId}
          />
          {/* tw-hidden input - the input above cannot accept both register() and getInputProps(),
              so we have another input registered and we set its value on line 48 when the shown one changes
              this ensures correct form integration with react-hook-form */}
          <input
            className="tw-hidden"
            {...register?.(name, { required: requiredErrorMessage })}
          />
          <button
            aria-label="search"
            type="submit"
            className={classNames(
              inputValue ? 'tw-text-black' : 'tw-text-grey-70',
              'tw-ml-4'
            )}
            {...getToggleButtonProps()}
            data-testid={dataTestId ? `${dataTestId}-Search-Button` : undefined}
          >
            <Icon
              name="magnifier"
              size={large ? '1.5rem' : '1.125rem'}
              className={darkMode ? 'tw-fill-white' : 'tw-fill-grey-70'}
            />
          </button>
        </div>
      </div>
      <ul
        className={classNames(
          { 'tw-hidden': !((showDropdown || isLoading) && isOpen) },
          'tw-absolute tw-z-40 tw-mt-2 tw-w-full tw-overflow-y-auto tw-rounded tw-border tw-border-grey-30 tw-bg-white tw-p-0 tw-shadow-md tw-transition-all',
          darkMode && 'tw-text-black',
          large ? 'tw-max-h-40' : 'tw-max-h-80'
        )}
        {...getMenuProps()}
      >
        {isLoading ? (
          <div className="tw-flex tw-h-36 tw-w-full tw-items-center tw-justify-center">
            <LoadingSpinner loadingLabel="loading" size={SizeEnum.Medium} />
          </div>
        ) : (
          showDropdown &&
          items.map((item, index) => {
            return (
              <li
                className={classNames(
                  highlightedIndex === index && 'tw-bg-grey-10',
                  'tw-cursor-pointer tw-px-xs tw-py-[14px]'
                )}
                key={`${item}-list-item`}
                {...getItemProps({ item, index })}
                data-testid={
                  dataTestId
                    ? `${dataTestId}-Search-Result-Item-${index}`
                    : undefined
                }
              >
                <HighlightedResult result={item} match={inputValue} />
              </li>
            );
          })
        )}
      </ul>
      {requiredErrorMessage && errors && (
        <ErrorMessage
          errors={errors}
          name="freetext"
          render={({ message }) => (
            <Text variant="label-12" className="tw-text-red">
              {message}
            </Text>
          )}
        />
      )}
    </div>
  );
};

export default Search;
